import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Link as MuiLink,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  getSampleColumns,
  renderSampleRow,
  formatNumericWithKind,
  orderedRanks,
  rankLabel,
  compareSortValues,
} from './sampleFindings';

/**
 * Specimen analysis tab.
 *
 * Renders the findings produced for one specimen, one section per pipeline
 * (latest non-superseded run). Presentational — the parent (SpecimenDetail)
 * fetches /api/analysis-findings/specimens/:id and passes the result down,
 * so the section count is available for the tab label without a second
 * request. Tables are kind-aware (via sampleFindings.js) and sortable; the
 * WUID column is dropped since the whole surface is for a single specimen.
 */

function formatUploader(run) {
  if (!run) return '—';
  if (run.uploaded_by_first_name || run.uploaded_by_last_name) {
    return `${run.uploaded_by_first_name || ''} ${run.uploaded_by_last_name || ''}`.trim();
  }
  return run.uploaded_by_username || '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const SpecimenAnalysisTab = ({ sections, loading, error }) => {
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading analysis findings…</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load analysis findings: {error}</Alert>;
  }

  if (!sections || sections.length === 0) {
    return (
      <Alert severity="info">
        No analysis findings yet for this specimen. Once a pipeline's outputs are
        ingested, they'll appear here.{' '}
        <MuiLink component={RouterLink} to="/analysis/upload">Upload analysis results</MuiLink>
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {sections.map((section) => (
        <PipelineSection key={section.run.run_id} section={section} />
      ))}
    </Stack>
  );
};

function PipelineSection({ section }) {
  const [expanded, setExpanded] = useState(true);
  const { pipeline, run, kind, findings } = section;

  return (
    <Paper elevation={1}>
      <Box
        sx={{ p: 2, display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <IconButton size="small" sx={{ mr: 1, mt: -0.5 }} aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">{pipeline.display_name}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {pipeline.version ? `v${pipeline.version} · ` : ''}
            <MuiLink
              component={RouterLink}
              to={`/analysis/runs/${run.run_number}`}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              Run #{run.run_number}
            </MuiLink>
            {' · '}{formatUploader(run)}{' · '}{formatDate(run.imported_at)}
          </Typography>
          {run.notes && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
              {run.notes}
            </Typography>
          )}
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <FindingsTable kind={kind} findings={findings} />
      </Collapse>
    </Paper>
  );
}

// Build the column model for a section's findings table. Each column carries
// a header, a display accessor, and a sort accessor. Taxa get one column per
// rank present (biological order) plus Abundance + Confidence; other kinds
// fall back to the generic single-column renderer from sampleFindings.
function buildColumns(kind, findings) {
  if (kind === 'taxon') {
    const ranks = orderedRanks(findings);
    const rankCols = ranks.map((rank) => ({
      header: rankLabel(rank),
      value: (f) => (f.payload && f.payload.taxonomy && f.payload.taxonomy[rank]) || '—',
      sort: (f) => (f.payload && f.payload.taxonomy && f.payload.taxonomy[rank]) || null,
    }));
    return [
      ...rankCols,
      {
        header: 'Abundance',
        value: (f) => formatNumericWithKind(f.payload && f.payload.abundance),
        sort: (f) => (f.payload && f.payload.abundance ? f.payload.abundance.value : null),
        defaultSort: 'desc', // lead with the most abundant taxa
      },
      {
        header: 'Confidence',
        value: (f) => formatNumericWithKind(f.payload && f.payload.confidence),
        sort: (f) => {
          const v = f.payload && f.payload.confidence ? f.payload.confidence.value : null;
          if (v == null) return null;
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        },
      },
    ];
  }
  // Generic fallback (classification, unknown kinds): one column per
  // getSampleColumns entry, value + sort from the rendered cell string.
  return getSampleColumns(kind).map((header, i) => ({
    header,
    value: (f) => renderSampleRow(f, kind)[i],
    sort: (f) => renderSampleRow(f, kind)[i],
  }));
}

function FindingsTable({ kind, findings }) {
  const columns = useMemo(() => buildColumns(kind, findings), [kind, findings]);

  // Default sort: first column flagged defaultSort (Abundance for taxa),
  // else the first column ascending.
  const [sort, setSort] = useState(() => {
    const idx = columns.findIndex((c) => c.defaultSort);
    return idx >= 0
      ? { columnIndex: idx, direction: columns[idx].defaultSort }
      : { columnIndex: 0, direction: 'asc' };
  });

  const sortedFindings = useMemo(() => {
    const col = columns[sort.columnIndex];
    const rows = [...(findings || [])];
    if (col) {
      rows.sort((a, b) => compareSortValues(col.sort(a), col.sort(b), sort.direction));
    }
    return rows;
  }, [findings, columns, sort]);

  const handleSort = (columnIndex) => {
    setSort((prev) =>
      prev.columnIndex === columnIndex
        ? { columnIndex, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { columnIndex, direction: 'asc' }
    );
  };

  if (!findings || findings.length === 0) {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No findings in this run for this specimen.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell key={c.header} sortDirection={sort.columnIndex === i ? sort.direction : false}>
                <TableSortLabel
                  active={sort.columnIndex === i}
                  direction={sort.columnIndex === i ? sort.direction : 'asc'}
                  onClick={() => handleSort(i)}
                >
                  {c.header}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedFindings.map((f) => (
            <TableRow key={f.finding_id} hover>
              {columns.map((c, j) => (
                <TableCell key={j}>{c.value(f)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SpecimenAnalysisTab;
